if (!customElements.get('sticky-product-cart')) {
  customElements.define('sticky-product-cart', class extends HTMLElement {
    connectedCallback() {
      this.root = document.getElementById(`ProductPage-${this.dataset.sectionId}`);
      this.button = this.querySelector('[data-sticky-cart-button]');
      if (!this.root || !this.button) return;

      this.mobile = matchMedia('(max-width: 749px)');
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      this.onClick = () => {
        this.update();
        if (this.hidden || this.button.disabled) return;
        const form = document.getElementById(`product-form-${this.dataset.sectionId}`);
        const productForm = form?.closest('product-form');
        if (productForm?.submitFrom) productForm.submitFrom(this.button);
        else if (this.sourceButton) form?.requestSubmit(this.sourceButton);
        this.update();
      };
      this.button.addEventListener('click', this.onClick);
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this.root, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'hidden', 'style', 'value'],
      });
      this.bodyObserver = new MutationObserver(this.schedule);
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this.root);
      this.resizeObserver.observe(this);
      this.dock = document.getElementById('mobile-dock');
      if (this.dock) this.resizeObserver.observe(this.dock);
      window.addEventListener('scroll', this.schedule, { passive: true });
      window.addEventListener('resize', this.schedule);
      document.addEventListener('focusin', this.schedule);
      document.addEventListener('focusout', this.schedule);
      this.root.addEventListener('change', this.schedule);
      this.mobile.addEventListener('change', this.schedule);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCartClose = subscribe(PUB_SUB_EVENTS.headerCartClose, () => {
          // The drawer restores focus before its hidden bar has had a paint.
          if (document.querySelector('mini-cart')?.openedBy !== this.button) return;
          this.update();
          if (!this.hidden) this.button.focus({ preventScroll: true });
        });
      }
      this.update();
    }

    isRendered(element) {
      return !!element?.getClientRects().length && !element.closest('[hidden], .hidden, .visually-hidden, .visibility-hidden') &&
        getComputedStyle(element).visibility !== 'hidden';
    }

    update() {
      // Product swaps briefly keep the old section attached with renamed IDs.
      if (document.getElementById(`ProductPage-${this.dataset.sectionId}`) !== this.root) {
        this.hidden = true;
        return;
      }
      const form = document.getElementById(`product-form-${this.dataset.sectionId}`);
      // Broadtime can replace the entire purchase fragment with Notify Me.
      this.sourceButton = form?.querySelector('[name="add"]');
      const source = this.sourceButton;
      const active = document.activeElement;
      const editing = active?.matches('textarea, select, [contenteditable="true"], input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"])');
      const overlay = document.body.matches('.evergreen-cart-open, .drawer-open, .overflow-hidden, .overflow-hidden-tablet');
      const visible = this.mobile.matches && this.isRendered(source) &&
        source.getBoundingClientRect().bottom <= 0 && !editing && !overlay;

      this.hidden = !visible;
      document.body.classList.toggle('sticky-product-cart-visible', visible);
      if (!visible) return;

      const variantInput = form.querySelector('[name="id"]');
      const busy = source.classList.contains('loading');
      this.button.disabled = source.disabled || source.getAttribute('aria-disabled') === 'true' ||
        busy || !variantInput?.value || variantInput.disabled;
      this.button.setAttribute('aria-busy', String(busy));
      this.button.classList.toggle('loading', busy);
      this.button.querySelector('.button-overlay-spinner')?.classList.toggle('hidden', !busy);
      this.setText('[data-sticky-cart-label]', source.querySelector(':scope > span')?.textContent || source.textContent);

      const title = this.root.querySelector('h1');
      if (title) {
        const titleCopy = title.cloneNode(true);
        titleCopy.querySelectorAll('.pdp-preorder-badge').forEach((badge) => badge.remove());
        this.setText('[data-sticky-cart-title]', titleCopy.textContent);
      }
      const price = document.getElementById(`price-${this.dataset.sectionId}`);
      const currentPrice = price?.querySelector('.price-on-sale .price-item-sale') ||
        price?.querySelector('.price-regular .price-item-regular');
      this.setText('[data-sticky-cart-price]', this.isRendered(currentPrice) ? currentPrice.textContent : '');

      const error = form.closest('product-form')?.querySelector('.product-form-error-message-wrapper');
      const errorText = this.isRendered(error) ? error.textContent.trim() : '';
      this.setText('[data-sticky-cart-error]', errorText);
      this.querySelector('[data-sticky-cart-error]').hidden = !errorText;

      const dockHeight = this.isRendered(this.dock) ? this.dock.getBoundingClientRect().height : 0;
      document.body.style.setProperty('--sticky-product-cart-dock-height', `${dockHeight}px`);
      document.body.style.setProperty('--sticky-product-cart-height', `${this.getBoundingClientRect().height}px`);
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      const text = (value || '').trim().replace(/\s+/g, ' ');
      if (element && element.textContent !== text) element.textContent = text;
    }

    disconnectedCallback() {
      if (!this.schedule) return;
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.bodyObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.button?.removeEventListener('click', this.onClick);
      window.removeEventListener('scroll', this.schedule);
      window.removeEventListener('resize', this.schedule);
      document.removeEventListener('focusin', this.schedule);
      document.removeEventListener('focusout', this.schedule);
      this.root?.removeEventListener('change', this.schedule);
      this.mobile?.removeEventListener('change', this.schedule);
      this.unsubscribeCartClose?.();
      const replacement = [...document.querySelectorAll('sticky-product-cart')].find((bar) => bar !== this && bar.schedule);
      if (replacement) {
        replacement.schedule();
        return;
      }
      document.body.classList.remove('sticky-product-cart-visible');
      document.body.style.removeProperty('--sticky-product-cart-height');
      document.body.style.removeProperty('--sticky-product-cart-dock-height');
    }
  });
}
